import { useEffect, useState } from "react"
import _ from "lodash"
import moment from "moment"

import { CUTOFF_TIME, DAY_START_TIME, TIME_STEP_MINUTES } from "./static"

const aMinutos = hora => {
  const partes = String(hora || "00:00").split(":")
  return (Number(partes[0]) || 0) * 60 + (Number(partes[1]) || 0)
}

const aHora = minutos =>
  `${String(Math.floor(minutos / 60)).padStart(2, "0")}:${String(
    minutos % 60
  ).padStart(2, "0")}`

/** Solo se puede pedir para hoy o para mañana, igual que el calendario anterior. */
export const opcionesFecha = () => {
  const hoy = moment()
  return [0, 1].map(dias => {
    const dia = moment(hoy).add(dias, "days")
    return {
      value: dia.format("YYYY-MM-DD"),
      label: `${dias === 0 ? "Hoy" : "Mañana"} — ${_.capitalize(
        dia.format("ddd DD MMM")
      )}`,
    }
  })
}

/**
 * El corte de las 19:00 solo aplica a la entrega con flete. Si se recoge en
 * granja no hay ruta que respetar y el horario se abre desde la hora actual.
 * El día siguiente está libre desde las 00:00.
 */
export const horaMinima = (fecha, esLocal) => {
  const manana = moment().add(1, "days").format("YYYY-MM-DD")
  if (fecha === manana) return DAY_START_TIME
  return esLocal ? moment().format("HH:mm") : CUTOFF_TIME
}

export const opcionesHora = (fecha, esLocal) => {
  const minimo = aMinutos(horaMinima(fecha, esLocal))
  const inicio = Math.ceil(minimo / TIME_STEP_MINUTES) * TIME_STEP_MINUTES

  const opciones = []
  for (let minutos = inicio; minutos < 24 * 60; minutos += TIME_STEP_MINUTES) {
    opciones.push({ value: aHora(minutos), label: `${aHora(minutos)} hrs` })
  }
  return opciones
}

/** Precio final que verá el cliente para una fila, según se entregue o se recoja. */
export const precioDeFila = (precio, esLocal) =>
  esLocal ? precio.unit_price_local : precio.unit_price_shipped

/**
 * Borrador del pedido de UN proveedor. Vive en la tarjeta del proveedor y se
 * reinicia cuando cambian sus filas (fecha, zona o cliente distintos).
 */
export default function useProviderOrderForm(rows, regionId) {
  const [cantidades, setCantidades] = useState({})
  const [priceSelected, setPriceSelected] = useState({})
  const [precios, setPrecios] = useState({})
  const [preciosLocal, setPreciosLocal] = useState({})
  const [observaciones, setObservaciones] = useState({})
  const [pickLocal, setPickLocal] = useState(false)
  const [fechaRecepcion, setFechaRecepcion] = useState(
    () => opcionesFecha()[0].value
  )
  const [horaRecepcion, setHoraRecepcion] = useState("")
  const [error, setError] = useState(null)

  useEffect(() => {
    const preciosInit = {}
    const preciosLocalInit = {}
    const seleccionInit = {}

    ;(rows || []).forEach(fila => {
      preciosInit[fila.id] = {}
      preciosLocalInit[fila.id] = {}

      // Con una sola zona no hay nada que elegir: se marca sola.
      if ((fila.prices || []).length === 1) {
        seleccionInit[fila.id] = fila.prices[0].id
      }

      ;(fila.prices || []).forEach(precio => {
        preciosInit[fila.id][precio.id] = precio.unit_price_shipped
        preciosLocalInit[fila.id][precio.id] = precio.unit_price_local
      })
    })

    setPrecios(preciosInit)
    setPreciosLocal(preciosLocalInit)
    setPriceSelected(seleccionInit)
    setCantidades({})
    setObservaciones({})
    setError(null)
  }, [rows])

  const horas = opcionesHora(fechaRecepcion, pickLocal)

  const ajustarHora = (fecha, esLocal) => {
    const minimo = horaMinima(fecha, esLocal)
    // Al apretar el mínimo, la hora ya elegida puede quedar fuera de rango.
    setHoraRecepcion(previo => (previo && previo < minimo ? "" : previo))
  }

  const setCantidad = (filaId, valor) =>
    setCantidades(previo => ({ ...previo, [filaId]: valor }))

  const setObservacion = (filaId, valor) =>
    setObservaciones(previo => ({ ...previo, [filaId]: valor }))

  const setPrecioSeleccionado = (filaId, precioId) =>
    setPriceSelected(previo => ({ ...previo, [filaId]: precioId }))

  const setPrecio = (filaId, precioId, valor) => {
    const numero = parseFloat(Number(valor).toFixed(2))
    if (pickLocal) {
      setPreciosLocal(previo => ({
        ...previo,
        [filaId]: { ...previo[filaId], [precioId]: numero },
      }))
      return
    }
    setPrecios(previo => ({
      ...previo,
      [filaId]: { ...previo[filaId], [precioId]: numero },
    }))
  }

  const togglePickLocal = valor => {
    setPickLocal(valor)
    ajustarHora(fechaRecepcion, valor)
  }

  const onChangeFecha = valor => {
    setFechaRecepcion(valor)
    ajustarHora(valor, pickLocal)
  }

  const reset = () => {
    setCantidades({})
    setObservaciones({})
    setError(null)
  }

  const pedidas = Object.keys(cantidades).filter(
    filaId => parseInt(cantidades[filaId], 10) > 0
  )

  /** Devuelve el mensaje de error, o null si el borrador es válido. */
  const validar = () => {
    if (pedidas.length === 0)
      return "Debes capturar la cantidad de al menos un producto."

    const sinPrecio = pedidas.some(filaId => priceSelected[filaId] == null)
    if (sinPrecio) return "Debes seleccionar un precio para el producto."

    if (!fechaRecepcion || !horaRecepcion)
      return "Debes colocar un horario de entrega"

    return null
  }

  const construirPayload = order => ({
    provider_id: rows[0].provider.id,
    farm_id: rows[0].farm.id,
    orders: pedidas.reduce(
      (acumulado, filaId) => ({
        ...acumulado,
        [filaId]: {
          value: cantidades[filaId],
          local: pickLocal,
          region_id: regionId,
        },
      }),
      {}
    ),
    prices: priceSelected,
    prices_amount: pickLocal ? preciosLocal : precios,
    observations: observaciones,
    pickLocal,
    timeDelivery: `${fechaRecepcion} ${horaRecepcion}`,
    order_id: order ? order.id : null,
  })

  return {
    state: {
      cantidades,
      priceSelected,
      precios,
      preciosLocal,
      observaciones,
      pickLocal,
      fechaRecepcion,
      horaRecepcion,
      fechas: opcionesFecha(),
      horas,
    },
    error,
    setError,
    events: {
      setCantidad,
      setObservacion,
      setPrecioSeleccionado,
      setPrecio,
      togglePickLocal,
      onChangeFecha,
      onChangeHora: setHoraRecepcion,
      reset,
    },
    validar,
    construirPayload,
  }
}
